"use client";

import type React from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";

interface PhoneInputFieldsProps {
  basePath: string;
}

const stripNonDigits = (value: string): string => value.replace(/\D/g, "");

const formatPhoneNumber = (value: string, isBrazil: boolean): string => {
  const digits = stripNonDigits(value);

  if (isBrazil) {
    return digits.slice(0, 9).replace(/(\d{5})(\d{1,4})$/, "$1-$2");
  }

  return digits.slice(0, 7).replace(/(\d{3})(\d{1,4})$/, "$1-$2");
};

const countryOptions = [
  { value: "55", label: "🇧🇷 +55", isBrazil: true },
  { value: "1", label: "🇺🇸 +1", isBrazil: false },
];

export function PhoneInputFields({ basePath }: PhoneInputFieldsProps) {
  const { control, watch, setValue } = useFormContext();
  // Watch for the stored value ("55" or "1"), default to "55"
  const currentInternationalCodeValue = watch(
    `${basePath}.internationalCode`,
    "55"
  );

  const selectedCountry =
    countryOptions.find((opt) => opt.value === currentInternationalCodeValue) ||
    countryOptions[0];

  const handleCountryChangeAndClearDependents = () => {
    setValue(`${basePath}.ddd`, "", { shouldValidate: true });
    setValue(`${basePath}.phoneNumber`, "", { shouldValidate: true });
  };

  return (
    <div
      className={cn(
        "flex items-stretch w-full rounded-md border border-input bg-transparent text-sm ring-offset-background",
        "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
      )}
    >
      <FormField
        control={control}
        name={`${basePath}.internationalCode`}
        render={({ field }) => (
          <FormItem className="flex-shrink-0 border-r border-input">
            <Select
              onValueChange={(newCodeValue: string) => {
                field.onChange(newCodeValue);
                handleCountryChangeAndClearDependents();
              }}
              value={field.value || "55"} // Stored value is "55" or "1"
            >
              <FormControl>
                <SelectTrigger
                  className={cn(
                    "h-full border-0 bg-transparent focus:ring-0 rounded-none rounded-l-md py-2 pl-3 pr-2",
                    "data-[placeholder]:text-muted-foreground"
                  )}
                  aria-label="Country code"
                >
                  {/* Display label (e.g., "🇧🇷 +55") based on current value */}
                  <SelectValue placeholder="País">
                    {
                      countryOptions.find(
                        (opt) => opt.value === (field.value || "55")
                      )?.label
                    }
                  </SelectValue>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {countryOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${basePath}.ddd`}
        render={({ field }) => (
          <FormItem className="flex-shrink-0 border-r border-input w-[70px]">
            <FormControl>
              <Input
                value={field.value || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(stripNonDigits(e.target.value).slice(0, 3))
                }
                onBlur={field.onBlur}
                disabled={field.disabled}
                name={field.name}
                ref={field.ref}
                placeholder="DDD"
                className="h-full w-full border-0 bg-transparent focus:ring-0 rounded-none text-center py-2 px-1"
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${basePath}.phoneNumber`}
        render={({ field }) => (
          <FormItem className="flex-grow">
            <FormControl>
              <Input
                value={formatPhoneNumber(field.value || "", selectedCountry.isBrazil)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(
                    stripNonDigits(e.target.value).slice(
                      0,
                      selectedCountry.isBrazil ? 9 : 7
                    )
                  )
                }
                onBlur={field.onBlur}
                disabled={field.disabled}
                name={field.name}
                ref={field.ref}
                type="tel"
                placeholder="Número"
                className="h-full w-full border-0 bg-transparent focus:ring-0 rounded-none rounded-r-md py-2 pl-3 pr-2"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}
